import Heading from 'molecules/Heading';
import Image from 'molecules/Image';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import Reveal from 'components/Animations/Reveal';

import { hasArrayValues } from 'utils/array/arrays';

import type { FeaturedSessionsConfigProps } from 'components/FeaturedSessions/config';
import type { FC } from 'react';

const FeaturedSessions: FC<FeaturedSessionsConfigProps> = props => {
  const {
    sectionProps,
    rest: { heading, backgroundImage, backgroundImageMobile, sessions },
  } = splitSectionProps(props);

  const mobileImage = backgroundImageMobile?.file?.url ? backgroundImageMobile : backgroundImage;

  const headingEl = heading ? <Heading {...heading} alignment="left" sidePadding="none" /> : null;

  const cardsEl = hasArrayValues(sessions) ? (
    <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
      {sessions.map((session, index) => (
        // Blue accent bar on the top edge; cards reveal in a soft stagger.
        <Reveal
          key={session.id ?? `session-${index}`}
          delay={index * 0.08}
          className="flex h-full flex-col gap-6 rounded-lg border-t-[6px] border-primary-1500 bg-white px-6 py-10"
        >
          {session?.title && (
            <div className="font-roboto text-xl font-semibold leading-[30px] text-gray-900">{session.title}</div>
          )}
          {session?.body && <div className="font-roboto text-md leading-6 text-gray-700">{session.body}</div>}
        </Reveal>
      ))}
    </div>
  ) : null;

  return (
    <Section {...sectionProps} bare paddingTop="none" paddingBottom="none" sidePadding="none">
      {/* Desktop (lg+): the full image flows so its whole height shows, with the
          heading and cards overlaid and vertically centered. */}
      <div className="relative hidden w-full lg:block">
        {backgroundImage?.file?.url && (
          <Image
            url={backgroundImage.file.url}
            alt={backgroundImage.alt || ''}
            noFill
            unsetMaxW
            unsetRatio
            width={backgroundImage.file.width}
            height={backgroundImage.file.height}
            className="h-auto w-full"
            sizes="100vw"
          />
        )}
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full max-w-screen-xl px-8 py-14">
            <div className="flex flex-col gap-12">
              {headingEl}
              {cardsEl}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile/tablet (<lg): the full image flows as a top banner at its natural
          aspect ratio, the heading overlays it, and the cards overlap its lower edge. */}
      <div className="lg:hidden">
        {mobileImage?.file?.url ? (
          <>
            <div className="relative h-[220px] w-full overflow-hidden sm:h-80">
              {/* The banner height is the visible part of the image (no black gap on
                  top). The image keeps its scale and is anchored to the top; the banner
                  clips the bottom. Adjust the banner height to show more/less. */}
              <Image
                url={mobileImage.file.url}
                alt={mobileImage.alt || ''}
                objectCover
                unsetRatio
                className="absolute inset-x-0 top-0 h-[360px] w-full sm:h-[460px] [&_img]:object-top"
                sizes="100vw"
              />
              <div className="absolute inset-0 flex items-center px-8 sm:px-32">{headingEl}</div>
            </div>
            <div className="relative z-10 -mt-20 px-8 pb-12 sm:px-32">{cardsEl}</div>
          </>
        ) : (
          <div className="px-8 py-12 sm:px-32">
            <div className="mb-8">{headingEl}</div>
            {cardsEl}
          </div>
        )}
      </div>
    </Section>
  );
};

export default FeaturedSessions;
